import { MessageSquare } from "lucide-react";
import type React from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { PriceDisplay } from "@/components/ui/price-display";
import { TierBadge } from "@/components/ui/tier-badge";
import {
  type AppointmentBookingStatus,
  type AppointmentOutcome,
  getBookingStatusInfo,
  getOutcomeStatusInfo,
} from "@/domains/appointment/appointments/shared/utils/appointment-status-style";
import type { AppointmentListFragment } from "@/gql/graphql";
import { useConfig } from "@/hooks/use-config";
import { useLoyaltyProgram } from "@/hooks/use-loyalty-program";
import { timezoneUtils } from "@/lib/timezone";
import { ColorUtils } from "@/lib/utils/color-utils";
import { useAppointmentTooltip } from "../hooks/use-appointment-tooltip";
import {
  calculateServicePosition,
  calculateServiceTimings,
  getServicesSummary,
  shouldDisplayAsServiceSegments,
} from "../utils/service-timing-utils";
import { CompactServiceDisplay, ServiceSegment } from "./ui/service-segment";

function getAppointmentDuration(appointment: AppointmentListFragment): number {
  const startTime = timezoneUtils.fromISO(appointment.startTime);
  const endTime = timezoneUtils.fromISO(appointment.endTime);
  return Math.round(endTime.diff(startTime, "minutes").minutes);
}

export interface CalendarAppointmentCardProps {
  appointment: AppointmentListFragment;
  style?: React.CSSProperties;
  className?: string;
  height: number;
  pixelsPerHour?: number;
  isDragging?: boolean;
  isSuggestionContext?: boolean;
  draggable?: boolean;
  children?: React.ReactNode;
  customStatusColor?: (status: string, isSuggestionContext: boolean) => string;
  onClick?: (appointment: AppointmentListFragment, e: React.MouseEvent) => void;
  onDragStart?: (e: React.DragEvent, appointment: AppointmentListFragment) => void;
  onDragEnd?: () => void;
}

export function CalendarAppointmentCard({
  appointment,
  style = {},
  className = "",
  height,
  pixelsPerHour = 48,
  isDragging = false,
  isSuggestionContext = false,
  onClick,
  onDragStart,
  onDragEnd,
  draggable = true,
  children,
  customStatusColor,
}: CalendarAppointmentCardProps) {
  // Tooltip functionality
  const { tooltip, handleMouseEnter, handleMouseLeave } = useAppointmentTooltip();

  // Get configuration for outcome tracking
  const { value: showOutcomeTracking = true } = useConfig("salon.outcome_tracking_enabled");
  const showLoyaltyTiers = useLoyaltyProgram();
  const { t } = useTranslation("newCalendar");

  // Get appointment status - only use outcome if outcome tracking is enabled
  const status = showOutcomeTracking
    ? appointment.outcome || appointment.bookingStatus
    : appointment.bookingStatus;

  // Get status color and background using appointment status utility
  const getStatusStyling = () => {
    if (customStatusColor) {
      return {
        colorClass: customStatusColor(status, isSuggestionContext),
        textClass: "text-card-foreground",
      };
    }

    // Determine if this is a booking status or outcome status
    const isBookingStatus = ["PENDING", "CONFIRMED", "DECLINED"].includes(status);
    const isOutcomeStatus =
      showOutcomeTracking &&
      ["COMPLETED", "IN_PROGRESS", "NO_SHOW", "RESCHEDULED"].includes(status);

    if (isBookingStatus) {
      const statusInfo = getBookingStatusInfo(status as AppointmentBookingStatus);
      return {
        colorClass: `border-l-4 ${statusInfo.borderColor} ${statusInfo.backgroundColor}`,
        textClass: statusInfo.textColor,
      };
    }
    if (isOutcomeStatus) {
      const statusInfo = getOutcomeStatusInfo(status as AppointmentOutcome);
      return {
        colorClass: `border-l-4 ${statusInfo.borderColor} ${statusInfo.backgroundColor}`,
        textClass: statusInfo.textColor,
      };
    }

    // Fallback for unknown status
    return {
      colorClass: "border-l-4 border-muted bg-muted",
      textClass: "text-foreground",
    };
  };

  const { colorClass: statusColorClass, textClass: statusTextClass } = getStatusStyling();

  // Build base classes
  const baseClasses = [
    "absolute",
    "rounded",
    "cursor-pointer",
    "transition-all",
    "p-1",
    "select-none",
    "border",
    "border-border",
    statusTextClass,
    "hover:scale-102",
    "hover:z-20",
    statusColorClass,
  ].filter(Boolean);

  // Add dragging state classes
  if (isDragging) {
    baseClasses.push("opacity-30", "z-50", "pointer-events-none");
  }

  // Combine all classes
  const allClasses = [...baseClasses, className].join(" ");

  // Handle click events
  const handleClick = (e: React.MouseEvent) => {
    // Only handle click if it's not from a touch device or programmatic
    if (e.detail === 0) return; // Programmatic click, ignore it
    onClick?.(appointment, e);
  };

  // Handle internal mouse enter for tooltip
  const handleInternalMouseEnter = (e: React.MouseEvent) => {
    handleMouseEnter(appointment, e);
  };

  // Handle drag start
  const handleDragStart = (e: React.DragEvent) => {
    onDragStart?.(e, appointment);
  };

  // Get display properties directly from fragment
  const clientName = appointment.customer
    ? `${appointment.customer.firstName} ${appointment.customer.lastName}`.trim()
    : "Unknown Client";
  const _serviceName = appointment.appointmentServices?.[0]?.serviceName || "Service";

  // Calculate service timings for VStack display
  const servicesWithTiming = calculateServiceTimings(appointment);
  const shouldShowServiceSegments =
    shouldDisplayAsServiceSegments(appointment, 20, pixelsPerHour) && height > 40; // Hide services if too small
  const _servicesSummary = getServicesSummary(servicesWithTiming);

  return (
    <>
      <button
        type="button"
        className={allClasses}
        style={{
          height: `${Math.max(height, 24)}px`,
          userSelect: "none",
          WebkitUserSelect: "none",
          MozUserSelect: "none",
          touchAction: "auto",
          ...style,
        }}
        draggable={draggable}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick(e as unknown as React.MouseEvent);
          }
        }}
        onMouseEnter={handleInternalMouseEnter}
        onMouseLeave={handleMouseLeave}
        onDragStart={handleDragStart}
        onDragEnd={onDragEnd}
        data-appointment-id={appointment.id}
      >
        {children || (
          <div className="flex flex-col h-full w-full overflow-hidden relative">
            {/* Customer name header (always show if space allows) */}
            {height > 20 && (
              <div className="px-2 py-1 bg-background/20 border-b border-border/20">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-1 flex-1 min-w-0">
                    <span className="font-medium truncate text-xs leading-none">{clientName}</span>
                    {appointment.customer?.totalVisits === 0 && height > 30 && (
                      <Badge
                        variant="secondary"
                        className="text-[10px] px-1 py-0 h-4 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 flex-shrink-0"
                      >
                        {t("common.newCustomer")}
                      </Badge>
                    )}
                    {appointment.customerMessage && (
                      <MessageSquare className="w-3 h-3 text-orange-600 dark:text-orange-400 flex-shrink-0" />
                    )}
                    {/* Tier badge inline if space allows */}
                    {showLoyaltyTiers && appointment.customer?.currentTier && height > 30 && (
                      <TierBadge
                        tier={appointment.customer.currentTier}
                        size="sm"
                        className="text-xs px-1 py-0.5 flex-shrink-0"
                      />
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Service segments or compact display */}
            <div className="flex-1 relative">
              {height > 30 ? (
                shouldShowServiceSegments || servicesWithTiming.length === 1 ? (
                  // Service segments for both single and multiple services
                  <div className="h-full flex flex-col">
                    {servicesWithTiming.map((service, index) => {
                      const isFirst = index === 0;
                      const isLast = index === servicesWithTiming.length - 1;
                      const availableHeight = height - (height > 20 ? 20 : 0); // Subtract header height
                      const segmentHeight = availableHeight / servicesWithTiming.length;

                      const position = {
                        ...calculateServicePosition(service, 0, 48),
                        height: Math.max(segmentHeight, 16), // Minimum 16px height
                        top: 0, // Position handled by flex layout
                      };

                      return (
                        <ServiceSegment
                          key={service.id}
                          service={service}
                          appointmentId={appointment.id}
                          position={position}
                          isFirst={isFirst}
                          isLast={isLast}
                          onClick={() => onClick?.(appointment, {} as React.MouseEvent)}
                          allowWorkerChange={false}
                        />
                      );
                    })}
                  </div>
                ) : (
                  // Compact display for single service or when space is limited
                  <CompactServiceDisplay
                    services={servicesWithTiming}
                    totalHeight={height - (height > 20 ? 20 : 0)}
                    onClick={() => onClick?.(appointment, {} as React.MouseEvent)}
                  />
                )
              ) : (
                // Very small appointment - just show client name
                <div className="flex items-center justify-center h-full px-2">
                  <span className="text-xs font-medium truncate">{clientName}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </button>
      {/* Render tooltip if visible */}
      {tooltip && (
        <AppointmentTooltip
          appointment={tooltip.appointment}
          position={tooltip.position}
          isVisible={tooltip.isVisible}
        />
      )}
    </>
  );
}

/**
 * Appointment tooltip for hover information
 */
export function AppointmentTooltip({
  appointment,
  position,
  isVisible,
}: {
  appointment: AppointmentListFragment;
  position: { x: number; y: number };
  isVisible: boolean;
}) {
  // Get configuration for outcome tracking
  const { value: showOutcomeTracking = true } = useConfig("salon.outcome_tracking_enabled");
  const showLoyaltyTiers = useLoyaltyProgram();

  if (!isVisible) return null;

  // Get appointment status - only use outcome if outcome tracking is enabled
  const status = showOutcomeTracking
    ? appointment.outcome || appointment.bookingStatus
    : appointment.bookingStatus;

  const clientName = appointment.customer
    ? `${appointment.customer.firstName} ${appointment.customer.lastName}`.trim()
    : "Unknown Client";

  // Calculate service timings for detailed tooltip
  const servicesWithTiming = calculateServiceTimings(appointment);

  const startTime = timezoneUtils.fromISO(appointment.startTime);
  const duration = getAppointmentDuration(appointment);

  const startTimeString = startTime.toFormat("HH:mm");
  const endTime = startTime.plus({ minutes: duration });
  const endTimeString = endTime.toFormat("HH:mm");

  return (
    <div
      className="fixed bg-card border border-border rounded-lg shadow-lg p-3 pointer-events-none"
      style={{
        left: position.x + 10,
        top: position.y - 10,
        maxWidth: "250px",
        zIndex: 9999,
      }}
    >
      <div className="space-y-2">
        {/* Customer and appointment overview */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="font-medium text-sm">{clientName}</div>
            {showLoyaltyTiers && appointment.customer?.currentTier && (
              <TierBadge tier={appointment.customer.currentTier} size="sm" />
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            {startTimeString} - {endTimeString}
          </div>
        </div>

        {/* Service timeline */}
        {servicesWithTiming.length > 0 && (
          <div className="text-xs">
            <div className="font-medium text-foreground mb-2">Service Timeline:</div>
            <div className="space-y-1">
              {servicesWithTiming.map((service, _index) => {
                const workerColor = ColorUtils.getColorStyle(service.worker?.color);
                const workerName = service.worker?.displayName ?? null;

                return (
                  <div key={service.id} className="flex items-center space-x-2 py-1">
                    {/* Time */}
                    <div className="text-xs text-muted-foreground min-w-[35px]">
                      {service.startTime.toFormat("HH:mm")}
                    </div>

                    {/* Worker indicator - only show if worker is assigned */}
                    {workerName && (
                      <div
                        className={`w-3 h-3 rounded-full ${workerColor.className} border border-background shadow-sm flex-shrink-0`}
                        style={workerColor.style}
                        title={workerName}
                      />
                    )}

                    {/* Service details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-medium truncate">{service.serviceName}</span>
                        <span className="ml-2 font-medium text-foreground">
                          <PriceDisplay amount={service.totalPrice} />
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-muted-foreground">
                        <span className="truncate">{service.serviceDuration}min</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Total */}
            <div className="border-t border-border mt-2 pt-2 flex justify-between font-medium">
              <span>Total:</span>
              <span>
                <PriceDisplay amount={appointment.totalPrice} />
              </span>
            </div>
          </div>
        )}

        <div className="text-xs">
          {(() => {
            // Determine if this is a booking status or outcome status
            const isBookingStatus = ["PENDING", "CONFIRMED", "DECLINED"].includes(status);
            const isOutcomeStatus =
              showOutcomeTracking &&
              ["COMPLETED", "IN_PROGRESS", "NO_SHOW", "RESCHEDULED"].includes(status);

            const hasWorker = appointment.appointmentServices?.some((s) => s.workerId != null);
            if (!hasWorker) {
              // Don't show anything for unassigned appointments
              return null;
            }

            let statusInfo: ReturnType<typeof getBookingStatusInfo> | undefined;
            if (isBookingStatus) {
              statusInfo = getBookingStatusInfo(status as AppointmentBookingStatus);
            } else if (isOutcomeStatus) {
              statusInfo = getOutcomeStatusInfo(status as AppointmentOutcome);
            } else {
              // Fallback for unknown status
              return (
                <span className="px-2 py-1 rounded text-xs bg-muted text-foreground">{status}</span>
              );
            }

            return (
              <span
                className={`px-2 py-1 rounded text-xs ${statusInfo.backgroundColor} ${statusInfo.textColor}`}
              >
                {statusInfo.text}
              </span>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
